import { QueryClient, QueryFunction } from "@tanstack/react-query";
import axios, { AxiosRequestConfig, AxiosResponse } from "axios";

async function throwIfResNotOk(res: Response) {
  if (!res.ok) {
    const text = (await res.text()) || res.statusText;
    throw new Error(`${res.status}: ${text}`);
  }
}

/**
 * Pull a human readable message out of whatever the backend returned.
 * The crypto backend answers with `{ status, message, data }` on success but can
 * fall back to FastAPI's `{ detail }`, a plain-text body, or a nested
 * `{ error: { message } }` depending on how the failure was raised.
 */
function extractErrorMessage(payload: unknown): string {
  if (!payload) return "";

  if (typeof payload === "string") {
    return payload.trim();
  }

  if (typeof payload !== "object") return String(payload);

  const body = payload as Record<string, any>;
  const direct = body.message ?? body.detail ?? body.error;
  if (typeof direct === "string" && direct.trim()) return direct.trim();

  if (direct && typeof direct === "object") {
    const nested = direct.message ?? direct.details;
    if (typeof nested === "string" && nested.trim()) return nested.trim();
    try {
      return JSON.stringify(nested);
    } catch {
      /* fall through */
    }
  }

  return "";
}

export async function apiRequest<T>(
  method: AxiosRequestConfig["method"],
  endpoint: string,
  data?: unknown,
  params?: Record<string, any>,
  backend_mode?: "stocks" | "crypto" | "tick",
  port?: number,
  responseType?: "json" | "blob" | "arraybuffer" | "text"
): Promise<T> {

  const isLocalhost =
    window.location.hostname === "localhost" ||
    window.location.hostname === "127.0.0.1";

  let baseUrl: string;

  if (isLocalhost) {
    const apiPort =
      port ??
      (backend_mode === "crypto"
        ? 8001
        : backend_mode === "tick"
        ? 8005 
        : 8002);
    baseUrl = `http://localhost:${apiPort}/`;
  } else {
    const prefix =
      backend_mode === "crypto"
        ? "crypto"
        : backend_mode === "tick"
        ? "tick"
        : "";
    baseUrl = `https://waliachandan.com/${prefix ? `${prefix}/` : ""}`;
  }

  const url = new URL(endpoint, baseUrl).toString();
  const token = localStorage.getItem("token");

  try {
    const headers: Record<string, string> = {};
    
    if (responseType !== "blob" && data) {
      headers["Content-Type"] = "application/json";
    }
    
    if (token) {
      headers["Authorization"] = `Bearer ${token}`;
    }

    const config: AxiosRequestConfig = {
      method,
      url,
      data,
      params,
      headers,
      responseType: responseType || "json",
    };

    const response: AxiosResponse<T> = await axios(config);
    return response.data;
  } catch (error: any) {
    if (error.response) {
      if (error.response.data instanceof Blob) {
        const errorText = await error.response.data.text();
        throw new Error(extractErrorMessage(errorText) || "Request failed");
      }
      throw new Error(
        extractErrorMessage(error.response.data) || `Request failed (${error.response.status})`
      );
    }
    throw error;
  }
}
type UnauthorizedBehavior = "returnNull" | "throw";
export const getQueryFn: <T>(options: {
  on401: UnauthorizedBehavior;
}) => QueryFunction<T> =
  ({ on401: unauthorizedBehavior }) =>
    async ({ queryKey }) => {
      const res = await fetch(queryKey[0] as string, {
        credentials: "include",
      });

      if (unauthorizedBehavior === "returnNull" && res.status === 401) {
        return null;
      }

      await throwIfResNotOk(res);
      return await res.json();
    };

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      queryFn: getQueryFn({ on401: "throw" }),
      refetchInterval: false,
      refetchOnWindowFocus: false,
      staleTime: Infinity,
      retry: false,
    },
    mutations: {
      retry: false,
    },
  },
});


export const tradingApi = <T,>(
  method: AxiosRequestConfig["method"],
  endpoint: string,
  currentTradingMode:"stocks" | "crypto" | "tick",
  data?: unknown,
  params?: Record<string, any>,
  responseType?: "json" | "blob" | "arraybuffer" | "text"
) => {
  return apiRequest<T>(
    method,
    endpoint,
    data,
    params,
    currentTradingMode === "crypto"
      ? "crypto"
      : currentTradingMode === "tick"
      ? "tick"
      : "stocks",
    undefined,
    responseType
  );
};
